import { sortByName } from "../../../utils/sortSelectOptions";

export { sortByName };

/** 主信息流可选的分类 type（与 CategoryEnum id 一致） */
export const FEED_CATEGORY_TYPE_IDS = [1, 17, 21];

/** 接口失败时的兜底，与后端 CategoryEnum 一致 */
export const FEED_TYPE_OPTIONS = [
  { categoryType: 1, name: "壁纸" },
  { categoryType: 17, name: "ai_assistant" },
  { categoryType: 21, name: "通用" },
];

const TYPE_LABEL_TO_ID = {
  壁纸: 1,
  ai_assistant: 17,
  通用: 21,
};

/**
 * 兼容 realRequest（axios 原始响应）与 request（拦截器已解包）两种返回形态。
 */
export function unwrapApiBody(res) {
  if (!res) {
    return null;
  }
  if (res.data && typeof res.data.success === "boolean") {
    return res.data;
  }
  if (typeof res.success === "boolean") {
    return res;
  }
  return null;
}

/**
 * 将接口返回的 category.type 统一为 CategoryEnum 数字 id。
 */
export function normalizeCategoryTypeId(typeValue) {
  if (typeValue === null || typeValue === undefined || typeValue === "") {
    return null;
  }
  const asNumber = Number(typeValue);
  if (!Number.isNaN(asNumber) && FEED_CATEGORY_TYPE_IDS.includes(asNumber)) {
    return asNumber;
  }
  return TYPE_LABEL_TO_ID[typeValue] ?? null;
}

export function isFeedCategory(cat) {
  return normalizeCategoryTypeId(cat && cat.type) !== null;
}

export function filterFeedCategories(categories) {
  return (categories || []).filter(isFeedCategory);
}

/**
 * 分类类型下拉：固定业务序 壁纸(1) → ai_assistant(17) → 通用(21)。
 */
export function sortFeedTypeOptions(typeOptions) {
  const order = new Map(
    FEED_CATEGORY_TYPE_IDS.map((id, index) => [id, index])
  );
  return (typeOptions || []).slice().sort((a, b) => {
    const leftOrder = order.has(a.categoryType)
      ? order.get(a.categoryType)
      : 999;
    const rightOrder = order.has(b.categoryType)
      ? order.get(b.categoryType)
      : 999;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return String(a.name || "").localeCompare(String(b.name || ""), "zh-CN");
  });
}

export function filterCategoriesByType(categories, categoryTypeKey) {
  if (categoryTypeKey === null || categoryTypeKey === undefined || categoryTypeKey === "") {
    return [];
  }
  const key = Number(categoryTypeKey);
  const matched = filterFeedCategories(categories).filter(
    (cat) => normalizeCategoryTypeId(cat.type) === key
  );
  return sortByName(matched);
}

export function filterFeedTypeOptions(typeOptions) {
  return (typeOptions || [])
    .map((item) => ({
      ...item,
      categoryType: Number(item.categoryType),
    }))
    .filter(
      (item) =>
        !Number.isNaN(item.categoryType) &&
        FEED_CATEGORY_TYPE_IDS.includes(item.categoryType)
    );
}

export function resolveFeedTypeOptionsFromApi(res) {
  const body = unwrapApiBody(res);
  if (body && body.success && Array.isArray(body.data)) {
    const parsed = filterFeedTypeOptions(body.data);
    if (parsed.length > 0) {
      return sortFeedTypeOptions(parsed);
    }
  }
  return sortFeedTypeOptions(FEED_TYPE_OPTIONS.slice());
}

export function normalizeResourceIdList(resourceId) {
  if (resourceId === null || resourceId === undefined || resourceId === "") {
    return [];
  }
  const ids = Array.isArray(resourceId) ? resourceId : [resourceId];
  // Number(null)/Number("") 为 0，需与 pageManagePageGroupHelper 一样过滤 <=0
  return ids
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);
}

export function findCategoryById(categories, categoryId) {
  if (categoryId === null || categoryId === undefined || categoryId === "") {
    return null;
  }
  const key = Number(categoryId);
  if (Number.isNaN(key)) {
    return null;
  }
  return (categories || []).find((cat) => Number(cat.id) === key) || null;
}

export function resolveCategoryTypeKey(categories, resourceId, preferredTypeKey) {
  const preferred = normalizeCategoryTypeId(preferredTypeKey);
  if (preferred !== null) {
    return preferred;
  }
  const ids = normalizeResourceIdList(resourceId);
  const firstId = ids[0];
  if (firstId === undefined) {
    return null;
  }
  const hit = findCategoryById(categories, firstId);
  return hit ? normalizeCategoryTypeId(hit.type) : null;
}

/**
 * 编辑回显：已绑定分类若不在 feed 过滤结果中，仍并入下拉选项避免 el-select 空白。
 */
export function mergeBoundCategoriesForComponent(categories, component) {
  const base = Array.isArray(categories) ? categories.slice() : [];
  if (!component) {
    return sortByName(base);
  }
  const boundIds = normalizeResourceIdList(component.resourceId);
  if (!boundIds.length) {
    return sortByName(base);
  }
  const merged = base.slice();
  // seen 已含 base 全部 id；!seen.has(id) 时 id 必不在 base，无需再 findCategoryById
  const seen = new Set(merged.map((cat) => Number(cat.id)));
  boundIds.forEach((id) => {
    if (seen.has(id)) {
      return;
    }
    merged.push({
      id,
      name: `分类 #${id}`,
      type: component.categoryTypeKey != null ? String(component.categoryTypeKey) : "",
    });
    seen.add(id);
  });
  return sortByName(merged);
}
